/**
 * Lightweight client-side A/B bucketing for CTA copy. Deterministic per
 * (anonymousId, experiment_key) — never calls any backend.
 *
 * The bucket survives across pageviews because it's keyed on the user's
 * PostHog distinct_id (when available) or a long-lived cookie set on first
 * touch. No personal data leaves the device.
 *
 * Variants reported via PostHog event `tool_cta_experiment_exposed` so we
 * can later read winners off the dashboard. No statistical engine required.
 */

import { track } from "@/lib/analytics";

const ANON_KEY = "bl_anon_id";

function getAnonId() {
    if (typeof window === "undefined") return "anon";
    try {
        if (window.posthog?.get_distinct_id) {
            const id = window.posthog.get_distinct_id();
            if (id) return id;
        }
        let v = window.localStorage.getItem(ANON_KEY);
        if (!v) {
            v = `a_${Math.random().toString(36).slice(2)}_${Date.now()}`;
            window.localStorage.setItem(ANON_KEY, v);
        }
        return v;
    } catch {
        return "anon";
    }
}

/** Get the backend URL for fire-and-forget ingest calls. */
function backendUrl() {
    if (typeof window === "undefined") return "";
    return process.env.REACT_APP_BACKEND_URL || "";
}

/** Fire-and-forget POST to /api/experiments/event. Never throws. */
function ingestEvent(payload) {
    if (typeof window === "undefined") return;
    try {
        const url = `${backendUrl()}/api/experiments/event`;
        const body = JSON.stringify(payload);
        // Use sendBeacon when available (survives pagehide); fall back to fetch.
        if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            if (navigator.sendBeacon(url, blob)) return;
        }
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body,
            keepalive: true,
        }).catch(() => {});
    } catch { /* non-critical */ }
}

/** Deterministic 32-bit hash (djb2) — same input always returns same bucket. */
function hash32(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h) ^ str.charCodeAt(i);
    }
    return h >>> 0;
}

/**
 * Promoted-winner cache.
 *
 * After the backend winner-scan auto-promotes an experiment, this cache
 * lets `pickVariant` short-circuit and serve 100% of traffic to the
 * winning variant — without changing call sites. Refreshed once per
 * session (TTL 5 min, matching the server Cache-Control).
 */
const WINNERS_TTL_MS = 5 * 60 * 1000;
let _winnersCache = null; // { fetched_at: number, map: {exp_id: variant_id} }
let _winnersInflight = null;

async function refreshWinners() {
    if (_winnersInflight) return _winnersInflight;
    _winnersInflight = (async () => {
        try {
            const r = await fetch(`${backendUrl()}/api/experiments/winners`, {
                method: "GET",
                credentials: "omit",
            });
            if (!r.ok) throw new Error(`status ${r.status}`);
            const data = await r.json();
            const map = {};
            for (const [exp_id, info] of Object.entries(data.winners || {})) {
                if (info?.variant_id) map[exp_id] = info.variant_id;
            }
            _winnersCache = { fetched_at: Date.now(), map };
        } catch {
            // Soft-fail — leave any prior cache in place, never block render.
            if (!_winnersCache) _winnersCache = { fetched_at: Date.now(), map: {} };
        } finally {
            _winnersInflight = null;
        }
    })();
    return _winnersInflight;
}

function winnersFresh() {
    return _winnersCache && (Date.now() - _winnersCache.fetched_at) < WINNERS_TTL_MS;
}

/** Returns the auto-promoted variant id for `experimentKey`, or null. */
function getPromotedWinner(experimentKey) {
    if (!winnersFresh()) {
        // Kick off refresh non-blockingly so the next render uses it.
        refreshWinners();
        if (!_winnersCache) return null;
    }
    return _winnersCache.map[experimentKey] || null;
}

/**
 * @param {string} experimentKey e.g. "cta_upload_v1"
 * @param {string[]} variants ordered list of variant ids; bucket = idx
 * @returns {string} variant id — the promoted winner if one exists, else
 *   the deterministic local bucket.
 */
export function pickVariant(experimentKey, variants) {
    if (!variants?.length) return "control";
    const winner = getPromotedWinner(experimentKey);
    if (winner && variants.includes(winner)) return winner;
    const anon = getAnonId();
    const bucket = hash32(`${anon}::${experimentKey}`) % variants.length;
    return variants[bucket];
}

const _exposed = new Set();
/**
 * Records exposure exactly once per (experiment, variant) per page mount.
 * Safe to call inline in render — guarded by an in-memory set.
 *
 * Dual-pipe: PostHog (for product analytics) + backend /experiments/event
 * (for the admin A/B testing dashboard).
 */
export function exposeVariant(experimentKey, variant, extra = {}) {
    const k = `${experimentKey}::${variant}`;
    if (_exposed.has(k)) return;
    _exposed.add(k);
    track("tool_cta_experiment_exposed", {
        experiment_key: experimentKey,
        variant,
        ...extra,
    });
    ingestEvent({
        experiment_id: experimentKey,
        variant_id: variant,
        event_name: "exposed",
        anonymous_id: getAnonId(),
        metadata: extra,
    });
}

/**
 * Record a conversion event for an A/B experiment. Mirrors the data into the
 * admin dashboard so non-PostHog admins can see funnels.
 */
export function recordConversion(experimentKey, variant, eventName, extra = {}) {
    ingestEvent({
        experiment_id: experimentKey,
        variant_id: variant,
        event_name: eventName,
        anonymous_id: getAnonId(),
        event_value: typeof extra.value === "number" ? extra.value : undefined,
        metadata: extra,
    });
}


/** Optional pre-fetch hook — call once from App.jsx on mount so the very
 *  first `pickVariant` already sees auto-promoted winners. */
export function prefetchPromotedWinners() {
    if (typeof window === "undefined") return;
    refreshWinners();
}
